import React from 'react';
import { Shield, Clock } from 'lucide-react';
import { GlassCard } from '../../ui/GlassCard';
import { AnimatedScore } from '../../ui/AnimatedScore';
import { RiskBadge } from './RiskBadge';
import type { ApiResult } from '../../../types/kyb.types';

interface ScoreGridProps {
  results: ApiResult;
}

export const ScoreGrid: React.FC<ScoreGridProps> = ({ results }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {/* Legitimacy Score Card */}
      <GlassCard className="p-6 text-center" gradient>
        <AnimatedScore 
          score={results.final_score} 
          label="Legitimacy" 
          size="lg" 
        />
        <p className="text-sm text-slate-600 mt-4">Out of 100</p>
      </GlassCard>

      {/* Risk Assessment Card */}
      <GlassCard className="p-6 text-center" gradient>
        <div className="mb-4">
          <Shield className="w-8 h-8 mx-auto text-blue-600 mb-2" />
          <h4 className="font-semibold text-slate-900">Risk Assessment</h4>
        </div>
        <RiskBadge 
          level={results.risk_level} 
          score={results.final_score} 
          size="md"
          showScore={true}
        />
        <p className="text-sm text-slate-600 mt-4">Score: {results.final_score}/100</p>
      </GlassCard>

      {/* Confidence Score Card */}
      <GlassCard className="p-6 text-center" gradient>
        <AnimatedScore 
          score={Math.round(results.confidence * 100)} 
          label="Confidence" 
          size="lg" 
        />
        <p className="text-sm text-slate-600 mt-4">Analysis Quality</p>
      </GlassCard>

      {/* Processing Time Card */}
      <GlassCard className="p-6 text-center" gradient>
        <div className="mb-4">
          <Clock className="w-12 h-12 mx-auto text-blue-600 mb-2" />
          <h4 className="font-semibold text-slate-900">Processing Time</h4>
        </div>
        <div className="text-3xl font-bold text-slate-900 mb-2">
          {results.processing_time_seconds.toFixed(2)}s
        </div>
        <p className="text-sm text-slate-600">Analysis Duration</p>
        {results.from_cache && (
          <span className="inline-block mt-3 px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
            Cached Result
          </span>
        )}
      </GlassCard>
    </div>
  );
};
